<!--eslint-disable max-len-->
<template>
  <div>
    <meta
      name="viewport"
      content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no"
    />
    <b-card
      bg-variant="light"
      v-bind:class="[marker ? validDiv : '', invalidDiv]"
    >
      <b-card-header style="font-weight: bold">{{ this.title }}</b-card-header>
      <div style="height: 550px">
        <template>
          <l-map
            ref="map"
            :crs="crs"
            :center="center"
            :zoom="zoom"
            :minZoom="minZoom"
            :maxZoom="maxZoom"
            :maxBounds="bounds"
            @click="click"
            style="height: 95%; width: 100%"
            @update:zoom="zoomUpdated"
          >
            <l-control position="topleft">
              <b-button
                @click="setCurrentLocation"
                :title="$t('map.currentLocation')"
              >
                <i class="fas fa-crosshairs"></i>
              </b-button>
            </l-control>
            <l-tile-layer
              :attribution="attribution"
              :options="{ maxZoom: 32 }"
              :url="url"
            ></l-tile-layer>
            <l-marker v-if="marker" :lat-lng="marker"></l-marker>
          </l-map>
        </template>
      </div>
    </b-card>
  </div>
</template>

<script>
import L from "leaflet";
import * as Vue2Leaflet from "vue2-leaflet";
import store from "@/store/store.js";
import { DateTime } from "luxon";

// fix marker ERR_404 (https://github.com/Leaflet/Leaflet/issues/4968#issuecomment-483402699)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

const mapURL =
  "https://wmts20.geo.admin.ch/1.0.0/ch.swisstopo.pixelkarte-farbe/default/current/3857/{z}/{x}/{y}.jpeg";

export default {
  components: {
    "l-map": Vue2Leaflet.LMap,
    "l-tile-layer": Vue2Leaflet.LTileLayer,
    "l-marker": Vue2Leaflet.LMarker,
    "l-control": Vue2Leaflet.LControl,
  },
  data() {
    return {
      validDiv: "validDiv",
      invalidDiv: "invalidDiv",
      zoomLevel: 8,
      minZoom: 8,
      maxZoom: 18,
      center: [46.8, 2.0],
      url: mapURL,
      attribution: "&copy; Swisstopo",
      crs: L.CRS.EPSG3857,
      bounds: [
        [47.9, 6.4],
        [45.7, 10.6],
      ],
    };
  },
  computed: {
    zoom: {
      get() {
        return this.zoomLevel;
      },
      set(zoom) {
        this.zoomLevel = zoom;
      },
    },
    marker: {
      get() {
        return store.getLocation();
      },
      set(marker) {
        store.setLocation(marker);
      },
    },
    inputIsValid() {
      return !!this.marker;
    },
  },
  props: {
    title: { type: String, default: "" },
  },
  created() {
    if (this.isMobile()) {
      this.minZoom = 7;
      this.zoomLevel = 7;
    }
  },
  mounted() {
    if (store.getLocation()) {
      this.setPositionAndZoom(store.getLocation());
    }
  },
  methods: {
    isMobile() {
      let check = false;
      (function (a) {
        if (
          //eslint-disable-next-line
          /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(
            a
          ) ||
          //eslint-disable-next-line
          /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(
            a.substr(0, 4)
          )
        )
          check = true;
      })(navigator.userAgent || navigator.vendor || window.opera);
      return check;
    },
    setPositionAndZoom(coordinates) {
      // zoom and center needs to be set on the map directly see:
      // https://github.com/vue-leaflet/Vue2Leaflet/issues/170
      if (this.zoom === this.minZoom) {
        this.$refs.map?.setZoom(13);
      }
      this.$refs.map?.setCenter(coordinates);
    },
    zoomUpdated(zoom) {
      this.zoom = zoom;
    },
    setPosition(position) {
      this.setPositionAndZoom([
        position.coords.latitude,
        position.coords.longitude,
      ]);
      this.marker = L.latLng(
        position.coords.latitude,
        position.coords.longitude
      );
    },
    setCurrentLocation() {
      navigator.geolocation.getCurrentPosition(this.setPosition);
      store.setDate(DateTime.now().toFormat("yyyy-MM-dd"));
    },
    click(evt) {
      this.marker = L.latLng(evt.latlng);
      this.center = [evt.latlng.lat, evt.latlng.lng];
    },
  },
};
</script>

<style scoped>
.container-fluid {
  padding-right: 0;
  padding-left: 0;
}
.leaflet-dragging .leaflet-grab {
  cursor: pointer;
}
.leaflet-grab {
  cursor: default;
}
.row {
  margin-left: 0;
  margin-top: 10px;
}
#leafletContainer {
  width: 100%;
  height: 95%;
  position: relative;
}
.invalidDiv {
  border-color: red;
}
.validDiv {
  border-color: green;
}
</style>
